import { BrowserPrintDeviceError, BrowserPrintTimeoutError } from "./errors";
import { getBrowserPrintApi } from "./runtime";
import type {
  BrowserPrintApi,
  BrowserPrintPrinterDevice,
  WindowLike,
} from "./types";

export const DEFAULT_TIMEOUT_MS = 4000;

export interface BrowserPrintApiOptions {
  api?: BrowserPrintApi;
  windowLike?: WindowLike;
  timeoutMs?: number;
}

function resolveTimeoutMs(timeoutMs?: number): number {
  if (
    typeof timeoutMs === "number" &&
    Number.isFinite(timeoutMs) &&
    timeoutMs > 0
  ) {
    return timeoutMs;
  }
  return DEFAULT_TIMEOUT_MS;
}

export function withTimeout<T>(
  timeoutMs: number | undefined,
  timeoutMessage: string,
  executor: (
    resolve: (value: T) => void,
    reject: (reason: unknown) => void,
  ) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const effectiveTimeoutMs = resolveTimeoutMs(timeoutMs);
    let settled = false;

    const timer = setTimeout(() => {
      if (settled) {
        return;
      }
      settled = true;
      reject(new BrowserPrintTimeoutError(timeoutMessage));
    }, effectiveTimeoutMs);

    const finishResolve = (value: T): void => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };

    const finishReject = (reason: unknown): void => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      reject(reason);
    };

    try {
      executor(finishResolve, finishReject);
    } catch (error) {
      finishReject(error);
    }
  });
}

export function resolveBrowserPrintApi(
  options: BrowserPrintApiOptions,
): BrowserPrintApi {
  return options.api ?? getBrowserPrintApi(options.windowLike);
}

export function normalizeErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallback;
}

export function normalizePrinterDevice(
  value: unknown,
  context: string,
): BrowserPrintPrinterDevice {
  if (typeof value !== "object" || value === null) {
    throw new BrowserPrintDeviceError(
      `${context} did not return a valid printer device object.`,
    );
  }

  const device = value as BrowserPrintPrinterDevice;
  if (typeof device.send !== "function") {
    throw new BrowserPrintDeviceError(
      `${context} returned device without a send(data, ...) function.`,
    );
  }

  const name =
    typeof device.name === "string" && device.name.trim().length > 0
      ? device.name
      : undefined;

  return {
    ...device,
    name,
  };
}
